import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import StaffMember from '../../../components/common/StaffMember';
import { useTasks } from '../hooks/useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

export default function TaskMoveConfirmationDialog({
  open,
  onClose,
  task,
  fromCategory,
  toCategory,
  onSuccess,
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const { updateTask, scheduleNextRecurringTask } = useTasks();
  const { showSuccess, showError } = useSnackbar();
  const [loading, setLoading] = useState(false);

  if (!task || !fromCategory || !toCategory) {
    return null;
  }

  const rrule = task.rrule ?? task.originalTask?.rrule;
  const isRecurring = !!rrule && (task.is_recurring || task.originalTask?.is_recurring);

  const handleConfirmThisTaskOnly = async () => {
    const taskId = task.id || task.originalTask?.task_id;
    if (!taskId) return;

    setLoading(true);
    try {
      // Schedule the next occurrence since this is "this task only"
      await scheduleNextRecurringTask(taskId);

      // Update the task to assign it to the new staff member and 
      // remove recurrance information as we only want to copy the task once
      const updateData = {
        primary_assignee_id: toCategory.id === 'unassigned' ? null : toCategory.id,
        isRecurring: false,
        rrule: null,
      };

      const result = await updateTask(taskId, updateData);

      if (result.success) {
        showSuccess(t('tasks:team_calendar.task_moved_successfully'));
        onSuccess?.({ detachedFromRecurrence: true });
        onClose();
      } else {
        showError(result.error || t('tasks:team_calendar.failed_to_move_task'));
      }
    } catch (error) {
      showError(t('tasks:team_calendar.failed_to_move_task'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAllTasks = async () => {
    const taskId = task.id || task.originalTask?.task_id;
    if (!taskId) return;

    setLoading(true);
    try {
      // Update the task to assign it to the new staff member
      const updateData = {
        primary_assignee_id: toCategory.id === 'unassigned' ? null : toCategory.id,
      };

      const result = await updateTask(taskId, updateData);

      if (result.success) {
        showSuccess(t('tasks:team_calendar.task_moved_successfully'));
        onSuccess?.({ detachedFromRecurrence: false });
        onClose();
      } else {
        showError(result.error || t('tasks:team_calendar.failed_to_move_task'));
      }
    } catch (error) {
      showError(t('tasks:team_calendar.failed_to_move_task'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('tasks:team_calendar.move_confirmation.title')}
      </DialogTitle>

      <DialogContent>
        {/* Task Information */}
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            {task.name || task.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {format(task.startDate || new Date(), 'PPP')}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* From/To Information */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('tasks:team_calendar.move_confirmation.movement')}
          </Typography>

          {/* From Category */}
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="body2" sx={{ minWidth: '50px', fontWeight: 600 }}>
              {t('common:from')}:
            </Typography>
            {fromCategory.id === 'unassigned' ? (
              <Typography variant="body2" color="text.secondary">
                {fromCategory.name}
              </Typography>
            ) : (
              <StaffMember
                staffMember={fromCategory.staffMember}
                avatarSize={24}
                imageSize="64x64"
                gap={1}
                nameVariant="body2"
              />
            )}
          </Box>

          {/* To Category */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ minWidth: '50px', fontWeight: 600 }}>
              {t('common:to')}:
            </Typography>
            {toCategory.id === 'unassigned' ? (
              <Typography variant="body2" color="text.secondary">
                {toCategory.name}
              </Typography>
            ) : (
              <StaffMember
                staffMember={toCategory.staffMember}
                avatarSize={24}
                imageSize="64x64"
                gap={1}
                nameVariant="body2"
              />
            )}
          </Box>
        </Box>

        {/* Recurring Task Info
        {isRecurring && (
          <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
            <Typography variant="body2" color="text.primary">
              {t('tasks:team_calendar.move_confirmation.recurring_note')}
            </Typography>
          </Box>
        )} */}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          {t('common:cancel')}
        </Button>

        {isRecurring && (
          <Button
            onClick={handleConfirmThisTaskOnly}
            variant="outlined"
            color="primary"
            disabled={loading}
          >
            {t('tasks:team_calendar.move_confirmation.this_task_only')}
          </Button>
        )}

        <Button
          onClick={handleConfirmAllTasks}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {isRecurring
            ? t('tasks:team_calendar.move_confirmation.all_tasks')
            : t('tasks:team_calendar.move_confirmation.move')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
